import {
  BitmapCoordinatesRenderingScope,
  CanvasRenderingTarget2D,
} from "fancy-canvas";
import {
  ICustomSeriesPaneRenderer,
  PaneRendererCustomData,
  PriceToCoordinateConverter,
  Time,
} from "lightweight-charts";

import { EarningsseriesData } from "./data";
import { EarningsseriesOptions } from "./options";

interface EarningsseriesItem {
  x: number;
  high: number;
  low: number;
  data: EarningsseriesData;
}

export class EarningsseriesRenderer implements ICustomSeriesPaneRenderer {
  _data: PaneRendererCustomData<Time, EarningsseriesData> | null = null;
  _options: EarningsseriesOptions | null = null;
  _series: any;

  constructor(series: any) {
    this._series = series;
  }

  draw(
    target: CanvasRenderingTarget2D,
    priceConverter: PriceToCoordinateConverter,
  ): void {
    target.useBitmapCoordinateSpace((scope) => {
      this._drawImpl(scope, priceConverter);
    });
  }

  update(
    data: PaneRendererCustomData<Time, EarningsseriesData>,
    options: EarningsseriesOptions,
  ): void {
    this._data = data;
    this._options = options;
  }

  _drawImpl(
    renderingScope: BitmapCoordinatesRenderingScope,
    priceToCoordinate: PriceToCoordinateConverter,
  ): void {
    if (
      this._data === null ||
      this._data.bars.length === 0 ||
      this._data.visibleRange === null ||
      this._options === null
    ) {
      return;
    }

    const bars: EarningsseriesItem[] = this._data.bars.map((bar) => ({
      x: bar.x * renderingScope.horizontalPixelRatio,
      high:
        priceToCoordinate(bar.originalData.high)! *
        renderingScope.verticalPixelRatio,
      low:
        priceToCoordinate(bar.originalData.low)! *
        renderingScope.verticalPixelRatio,
      data: bar.originalData,
    }));

    this._series.renderedMarkers = [];

    const ctx = renderingScope.context;
    const scaleX = renderingScope.horizontalPixelRatio;
    const scaleY = renderingScope.verticalPixelRatio;
    let themeColor;
    // 🔥 ZOOM SCALE (based on bar spacing)
    const zoomScale = getZoomScale(this._data.barSpacing);
    const uiScale = zoomScale * scaleY;

    for (
      let i = this._data.visibleRange.from;
      i < this._data.visibleRange.to;
      i++
    ) {
      const bar = bars[i];

      // detect first bar of earnings series
      const isStartOfEarnings =
        bar.data.isEarningsDay &&
        (i === this._data.visibleRange.from ||
          !bars[i - 1]?.data.isEarningsDay);

      const markerY = renderingScope.bitmapSize.height - 50 * scaleY;
      const markerX = bar.x;

      if (isStartOfEarnings) {
        themeColor = this._data.bars[i].barColor || "#dc2626";
        // ----- SMALL E MARKER -----
        const markerRadius = 6 * uiScale;
        ctx.beginPath();
        ctx.arc(markerX, markerY, markerRadius, 0, Math.PI * 2);
        ctx.fillStyle = themeColor;
        ctx.fill();

        ctx.fillStyle = "#ffffff";
        ctx.font = `600 ${8 * uiScale}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("E", markerX, markerY);

        this._series.renderedMarkers.push({
          x: markerX / scaleX,
          y: markerY / scaleY,
          data: bar.data,
        });

        // ----- ZOOM-AWARE RECTANGLE -----
        drawEarningsRect(
          ctx,
          markerX + 10 * uiScale,
          markerY - 6 * uiScale,
          {
            status: this._data.bars[i].originalData.status || "",
            scaledReturn: this._data.bars[i].originalData.scaledReturn || 0,
            sigma: this._data.bars[i].originalData.sigma || 0,
            earningsCallUrl:
              this._data.bars[i].originalData.earningsCallUrl || "",
            date: this._data.bars[i].originalData.date || "",
            companyName: this._data.bars[i].originalData.companyName || "",
          },
          uiScale,
          themeColor,
        );
      }

      // ----- EARNINGS SHADING -----
      if (bar.data.isEarningsDay) {
        ctx.fillStyle = `${themeColor}`; // 33 is ~20% alpha in hex
        ctx.fillRect(
          markerX,
          markerY,
          this._data.barSpacing * renderingScope.horizontalPixelRatio,
          500,
        );
      }
    }
  }
}

/* ───────────────────────────────────────────── */
/* ZOOM HELPERS                                  */
/* ───────────────────────────────────────────── */

function getZoomScale(barSpacing: number) {
  const baseSpacing = 8; // normal zoom
  const scale = barSpacing / baseSpacing;
  return Math.min(Math.max(scale, 0.75), 1.6);
}

/* ───────────────────────────────────────────── */
/* RECTANGLE RENDERER                            */
/* ───────────────────────────────────────────── */

type EarningsRectData = {
  status?: string;
  scaledReturn: number;
  sigma: number;
  earningsCallUrl: string;
  date?: string;
  companyName?: string;
};

function drawEarningsRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number, // bottom-left anchor
  data: EarningsRectData,
  scale = 1,
  themeColor = "#dc2626",
) {
  const padding = 16 * scale;
  const lineGap = 8 * scale;
  const radius = 10 * scale;
  const maxWidth = 260 * scale;

  // -------- Text setup --------
  const titleFont = `600 ${16 * scale}px Inter, system-ui, sans-serif`;
  const bodyFont = `${14 * scale}px Inter, system-ui, sans-serif`;

  const lines = [
    { text: "Volatility Score", font: titleFont },
    { text: "Earnings Event", value: data?.date },
    { text: "Company", value: data?.companyName },
    {
      text: "Number",
      value: `${data?.scaledReturn.toFixed(2)}`,
    },
    {
      text: "Sigma",
      value: data?.sigma.toFixed(4),
    },
    {
      text: "Status",
      value: data?.status,
      highlight: true,
    },
  ];

  // -------- Measure height --------
  let height = padding * 2;

  ctx.font = titleFont;
  height +=
    ctx.measureText(lines[0].text).actualBoundingBoxAscent + lineGap * 2;

  ctx.font = bodyFont;
  height += (lines.length - 1) * (14 * scale + lineGap);

  const topY = y - height;

  // -------- Card --------
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.25)";
  ctx.shadowBlur = 12 * scale;
  ctx.shadowOffsetY = 4 * scale;

  drawRoundedRect(ctx, x, topY, maxWidth, height, radius);
  ctx.fillStyle = themeColor;
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = "rgba(255,255,255,0.15)";
  ctx.lineWidth = 1 * scale;
  drawRoundedRect(ctx, x, topY, maxWidth, height, radius);
  ctx.stroke();

  // -------- Text --------
  let cursorY = topY + padding;

  // Title
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.font = titleFont;
  ctx.fillStyle = "#f9fafb";
  ctx.fillText(lines[0].text, x + padding, cursorY);
  cursorY += 18 * scale;

  // -------- Rows --------
  ctx.font = bodyFont;

  for (let i = 1; i < lines.length; i++) {
    const row = lines[i];

    // Label
    ctx.textAlign = "left";
    ctx.fillStyle = "#e5e7eb";
    ctx.fillText(row.text, x + padding, cursorY);

    // Value
    ctx.textAlign = "right";

    if (row.highlight) {
      const text = row.value!;

      ctx.font = bodyFont;
      const metrics = ctx.measureText(text);

      const pillPaddingX = 10 * scale;
      const pillPaddingY = 6 * scale;

      const textHeight =
        metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

      const pillHeight = textHeight + pillPaddingY * 2;
      const pillWidth = metrics.width + pillPaddingX * 2;

      const pillX = x + maxWidth - padding - pillWidth;

      // 👇 This centers the pill around the text baseline
      const pillY = cursorY - metrics.actualBoundingBoxAscent - pillPaddingY;

      ctx.save();

      // Pill background
      ctx.fillStyle = "#ffffff";
      drawRoundedRect(ctx, pillX, pillY, pillWidth, pillHeight, 999);
      ctx.fill();

      // Text
      ctx.fillStyle = "#316acd";
      ctx.textAlign = "right";
      ctx.fillText(text, x + maxWidth - padding - pillPaddingX, cursorY);

      ctx.restore();
    } else {
      ctx.fillStyle = "#c7d2fe";
      ctx.fillText(row.value!, x + maxWidth - padding, cursorY);
    }

    ctx.textAlign = "left";
    cursorY += 14 * scale + lineGap;
  }
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
